import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTenant } from "../../contexts/TenantContext";
import { formatLocalDate } from "../../utils/dateUtils";
import useCoursePermissions from "../../hooks/useCoursePermissions";
import {
  Box, Typography, IconButton, Button, Paper, CircularProgress,
  Select, MenuItem, FormControl,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import HowToGuideBox from "../../components/HowToGuideBox";
import {
  fetchAttendanceForSession,
  updateAttendanceRecord,
  updateSession,
  fetchSessionsForCourse,
  fetchClassById,
  fetchRegistrationsForClass,
} from "../../api/backend";
import { useTheme, alpha } from "@mui/material/styles";

/** Prefer stored sessionNumber; else 1-based position in date order — never raw DB id. */
export function sessionDisplayNumber(session, allSessions = []) {
  if (session == null) return null;
  if (session.sessionNumber != null && session.sessionNumber !== "") {
    return session.sessionNumber;
  }
  const sorted = [...allSessions].sort((a, b) => {
    const da = new Date(a.sessionDate || a.date || 0).getTime();
    const db = new Date(b.sessionDate || b.date || 0).getTime();
    if (da !== db) return da - db;
    return Number(a.id) - Number(b.id);
  });
  const idx = sorted.findIndex((s) => String(s.id) === String(session.id));
  return idx >= 0 ? idx + 1 : null;
}

const STATUS_API = {
  present: "Present",
  tardy: "Tardy",
  noshow: "No-Show",
  callout: "Call-Out",
  excused: "Excused",
};

/** Normalize API / UI status → STATUS_META key, or null if unmarked. */
export function getRecordStatus(r) {
  if (!r) return null;
  const raw = r.status;
  if (raw == null || raw === "") {
    // Explicit unmarked placeholder, or attended-only without status
    if (r.attended === true || r.checkedInAt || r.signInTime) return "present";
    return null;
  }
  const s = String(raw).toLowerCase().replace(/[-\s]/g, "");
  if (s === "present") return "present";
  if (s === "tardy") return "tardy";
  if (s === "noshow") return "noshow";
  if (s === "callout") return "callout";
  if (s === "excused") return "excused";
  if (r.attended === true || r.checkedInAt || r.signInTime) return "present";
  return null;
}

/** Merge enrolled roster with session attendance so instructors can mark anyone. */
export function mergeRosterAttendance(registrations, attendanceRecords, sessionId) {
  const byUser = new Map();
  (attendanceRecords || []).forEach((a) => {
    if (a?.userId != null) byUser.set(String(a.userId), a);
  });

  const enrolled = (registrations || []).filter((r) => {
    const st = r.enrollmentStatus || r.status;
    return st !== "WAITLISTED";
  });

  const rows = enrolled.map((reg) => {
    const uid = reg.userId;
    const existing = byUser.get(String(uid));
    const name =
      existing?.name ||
      reg.name ||
      reg.fullName ||
      reg.user?.name ||
      reg.user?.username ||
      `User ${uid}`;
    const email = existing?.email || reg.email || reg.user?.email || null;
    const agencyId = existing?.agencyId || reg.agencyId || reg.user?.agencyId || null;
    if (existing) {
      byUser.delete(String(uid));
      return {
        ...existing,
        name: existing.name || name,
        email: existing.email || email,
        agencyId: existing.agencyId || agencyId,
      };
    }
    return {
      userId: uid,
      sessionId: sessionId != null ? Number(sessionId) || sessionId : null,
      name,
      email,
      agencyId,
      status: null,
      attended: false,
      checkedInAt: null,
      checkedOutAt: null,
      signInTime: null,
      signOutTime: null,
    };
  });

  // Keep any attendance rows for users no longer on roster
  byUser.forEach((a) => rows.push(a));

  return rows.sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" }),
  );
}

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  ink: "var(--color-ink)",
  ink2: "var(--color-ink-2)",
  ink3: "var(--color-ink-3)",
  ink4: "var(--color-ink-4)",
  line: "var(--color-surface-press-light)",
  line2: "var(--color-surface-press-light)",
  line3: "var(--color-surface-2)",
  surface: "#FFFFFF",
  surface2: "var(--color-surface-2)",
  surface3: "var(--color-surface-2)",
  pageBg: "#ffffff",
  present: "#15803D",
  presentBg: "rgba(21,128,61,.10)",
  presentBr: "rgba(21,128,61,.22)",
  tardy: "#B5651D",
  tardyBg: "rgba(181,101,29,.12)",
  tardyBr: "rgba(181,101,29,.24)",
  noshow: "#B42318",
  noshowBg: "rgba(180,35,24,.10)",
  noshowBr: "rgba(180,35,24,.22)",
  callout: "#C2410C",
  calloutBg: "rgba(194,65,12,.10)",
  calloutBr: "rgba(194,65,12,.22)",
  excused: "var(--color-primary)",
  excusedBg: "rgba(37,99,235,.10)",
  excusedBr: "rgba(37,99,235,.22)",
  unmarked: "var(--color-ink-3)",
  unmarkedBg: "rgba(123,132,153,.10)",
  live: "#C2410C",
  liveBg: "var(--color-warn-bg)",
  liveBr: "rgba(194,65,12,.22)",
  amber100: "rgba(181,101,29,.20)",
  shadowSm: "0 1px 0 rgba(20,24,40,.04), 0 1px 3px rgba(20,24,40,.06)",
  shadowMd: "0 8px 24px -10px rgba(20,24,40,.15)",
};

const STATUS_META = {
  present:  { label: "Present",  color: T.present,  bg: T.presentBg,  br: T.presentBr,  key: "present" },
  tardy:    { label: "Tardy",    color: T.tardy,    bg: T.tardyBg,    br: T.tardyBr,    key: "tardy" },
  noshow:   { label: "No-show",  color: T.noshow,   bg: T.noshowBg,   br: T.noshowBr,   key: "noshow" },
  callout:  { label: "Call-out", color: T.callout,  bg: T.calloutBg,  br: T.calloutBr,  key: "callout" },
  excused:  { label: "Excused",  color: T.excused,  bg: T.excusedBg,  br: T.excusedBr,  key: "excused" },
};
const STATUS_KEYS = ["present", "tardy", "noshow", "callout", "excused"];

const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #B5651D, #C2410C)",
  "linear-gradient(135deg, var(--color-primary), var(--color-primary))",
  "linear-gradient(135deg, #15803D, var(--color-accent-violet))",
  "linear-gradient(135deg, var(--color-accent-violet), var(--color-primary))",
  "linear-gradient(135deg, #B42318, #C2410C)",
  "linear-gradient(135deg, var(--color-ink-2), var(--color-ink))",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function initials(name) {
  return (name || "").split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

function avatarBg(name) {
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

function formatTimeOnly(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function sessionStateText(session) {
  if (!session) return "Unknown";
  if (session.locked) return "Locked";
  if (session.enabled === false) return "Closed";
  const now = new Date();
  const start = session.startTime ? new Date(`${session.sessionDate || session.date}T${session.startTime}`) : null;
  const end = session.endTime ? new Date(`${session.sessionDate || session.date}T${session.endTime}`) : null;
  if (start && now >= start && end && now <= end) return "Live";
  if (end && now > end) return "Ended";
  if (start && now < start) return "Upcoming";
  return "Open";
}

function sessionStateColor(state) {
  if (state === "Live") return { color: T.live, bg: T.liveBg, br: T.liveBr };
  if (state === "Open") return { color: T.present, bg: T.presentBg, br: T.presentBr };
  if (state === "Ended" || state === "Locked") return { color: T.ink3, bg: T.unmarkedBg, br: "rgba(123,132,153,.20)" };
  return { color: T.ink3, bg: T.unmarkedBg, br: "rgba(123,132,153,.20)" };
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function AttendanceRosterPage() {
  const { id: courseId, sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { effectiveUser } = useTenant();
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tAccent = theme.palette.warning.main;

  const [records, setRecords] = useState([]);
  const [session, setSession] = useState(location.state?.session || null);
  const [allSessions, setAllSessions] = useState([]);
  const [course, setCourse] = useState(location.state?.course || (location.state?.courseId ? { id: location.state.courseId, title: location.state.courseTitle } : null));
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [lockLoading, setLockLoading] = useState(false);
  const [lockMessage, setLockMessage] = useState("");
  const [savedPulse, setSavedPulse] = useState(false);

  // Resolve session from URL id (source of truth) — never trust list-index vs DB-id label mismatch
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const stateSession = location.state?.session || null;
    const stateCourse = location.state?.course
      || (location.state?.courseId ? { id: location.state.courseId, title: location.state.courseTitle } : null);
    Promise.all([
      fetchAttendanceForSession(sessionId).catch(() => []),
      fetchSessionsForCourse(courseId).catch(() => []),
      fetchRegistrationsForClass(courseId).catch(() => []),
      stateCourse
        ? Promise.resolve(stateCourse)
        : Promise.resolve()
            .then(() => fetchClassById(courseId))
            .catch(() => null),
    ])
      .then(([att, sessionsList, regs, courseData]) => {
        if (cancelled) return;
        const list = Array.isArray(sessionsList) ? sessionsList : [];
        setAllSessions(list);
        const match = list.find((s) => String(s.id) === String(sessionId));
        setSession(match || stateSession || null);
        if (courseData) setCourse(courseData);
        setRecords(mergeRosterAttendance(regs, att, sessionId));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [sessionId, courseId, location.state]);

  // Permissions
  const { isInstructor, isCourseCoordinator, isAdmin } = useCoursePermissions(
    effectiveUser,
    course,
  );
  const sessionLocked = Boolean(session?.locked);
  const sessionDisabled = session?.enabled === false;
  const canLockSession = isInstructor || isCourseCoordinator || isAdmin;
  const canEditAttendance = !sessionLocked && !sessionDisabled;

  // Summary counts
  const summary = useMemo(() => {
    const counts = { present: 0, tardy: 0, noshow: 0, callout: 0, excused: 0, unmarked: 0 };
    records.forEach((r) => {
      const s = getRecordStatus(r);
      if (s && counts[s] !== undefined) counts[s]++;
      else counts.unmarked++;
    });
    return counts;
  }, [records]);

  const totalStudents = records.length;
  const markedCount = totalStudents - summary.unmarked;
  const markedPct = totalStudents > 0 ? Math.round((markedCount / totalStudents) * 100) : 0;

  // Filtered records
  const filtered = useMemo(() => {
    let out = records;
    if (statusFilter === "unmarked") {
      out = out.filter((r) => !getRecordStatus(r));
    } else if (statusFilter && statusFilter !== "all") {
      out = out.filter((r) => getRecordStatus(r) === statusFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((r) => {
        const name = (r.name || r.studentName || "").toLowerCase();
        const agency = (r.agencyId || "").toLowerCase();
        return name.includes(q) || agency.includes(q);
      });
    }
    return out;
  }, [records, statusFilter, search]);

  const filterTabs = useMemo(() => [
    { id: "all", label: "All", count: totalStudents },
    ...STATUS_KEYS.map((k) => ({ id: k, label: STATUS_META[k].label, count: summary[k] })),
    { id: "unmarked", label: "Unmarked", count: summary.unmarked },
  ], [totalStudents, summary]);

  // Update a single student's status
  const updateStatus = useCallback(async (record, newStatus) => {
    if (!canEditAttendance) return;
    const currentStatus = getRecordStatus(record);
    const target = currentStatus === newStatus ? null : newStatus;
    try {
      const payload = { status: target ? (STATUS_API[target] || target) : null };
      const updated = await updateAttendanceRecord(sessionId, record.userId, payload);
      setRecords((prev) => prev.map((r) => (String(r.userId) === String(updated.userId) ? { ...r, ...updated, name: updated.name || r.name, email: updated.email || r.email, agencyId: updated.agencyId || r.agencyId } : r)));
      setSavedPulse(true);
      setTimeout(() => setSavedPulse(false), 1200);
    } catch (e) {
      // silent fail; could show toast
    }
  }, [canEditAttendance, sessionId]);

  // Mark all unmarked as present
  const markAllPresent = useCallback(async () => {
    if (!canEditAttendance) return;
    const toUpdate = records.filter((r) => !getRecordStatus(r));
    const updated = await Promise.all(
      toUpdate.map((r) =>
        updateAttendanceRecord(sessionId, r.userId, { status: "Present" }).catch(() => null),
      ),
    );
    setRecords((prev) => {
      const map = new Map(prev.map((r) => [String(r.userId), r]));
      updated.forEach((u) => {
        if (u) {
          const existing = map.get(String(u.userId));
          map.set(String(u.userId), { ...existing, ...u, name: u.name || existing?.name, email: u.email || existing?.email });
        }
      });
      return Array.from(map.values());
    });
    setSavedPulse(true);
    setTimeout(() => setSavedPulse(false), 1200);
  }, [canEditAttendance, records, sessionId]);

  // Bulk update selected students
  const bulkUpdate = useCallback(async (newStatus) => {
    if (!canEditAttendance || selectedIds.size === 0) return;
    const apiStatus = STATUS_API[newStatus] || (newStatus.charAt(0).toUpperCase() + newStatus.slice(1));
    const toUpdate = records.filter((r) => selectedIds.has(r.userId));
    const updated = await Promise.all(
      toUpdate.map((r) =>
        updateAttendanceRecord(sessionId, r.userId, { status: apiStatus }).catch(() => null),
      ),
    );
    setRecords((prev) => {
      const map = new Map(prev.map((r) => [String(r.userId), r]));
      updated.forEach((u) => {
        if (u) {
          const existing = map.get(String(u.userId));
          map.set(String(u.userId), { ...existing, ...u, name: u.name || existing?.name, email: u.email || existing?.email });
        }
      });
      return Array.from(map.values());
    });
    setSelectedIds(new Set());
    setSavedPulse(true);
    setTimeout(() => setSavedPulse(false), 1200);
  }, [canEditAttendance, records, selectedIds, sessionId]);

  // Toggle lock
  const handleToggleLock = async () => {
    if (!session) return;
    setLockLoading(true);
    setLockMessage("");
    try {
      const updated = await updateSession(session.id, { locked: !session.locked });
      setSession(updated);
      setLockMessage(updated.locked ? "Session locked. Attendance is now read-only." : "Session unlocked. Attendance can be edited again.");
    } catch (err) {
      setLockMessage(err.message || "Unable to update session lock state.");
    } finally {
      setLockLoading(false);
    }
  };

  const allSelected = filtered.length > 0 && filtered.every((r) => selectedIds.has(r.userId));
  const selectAllVisible = () => {
    const visibleIds = filtered.map((r) => r.userId);
    setSelectedIds(new Set(visibleIds));
  };
  const clearSelection = () => setSelectedIds(new Set());
  const toggleSelect = (userId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const courseLabel = course ? course.title || course.name || `Course ${courseId}` : `Course ${courseId}`;
  const sessionDateLabel = formatLocalDate(session?.sessionDate, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  const displayNum = sessionDisplayNumber(session, allSessions);
  const sessionLabel = displayNum != null ? `Session ${displayNum}` : "Session";
  const sState = sessionStateText(session);
  const sStateColor = sessionStateColor(sState);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: "16px", md: "40px" }, py: "26px", pb: "80px", bgcolor: T.pageBg, minHeight: "100vh" }}>
      {/* Breadcrumb */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: 13, fontWeight: 500, color: T.ink3, mb: "14px", flexWrap: "wrap" }}>
        <Box component="span" onClick={() => navigate("/course-management")} sx={{ color: tPrimary, fontWeight: 600, cursor: "pointer", borderRadius: "6px", px: "6px", py: "4px", "&:hover": { background: alpha(tPrimary, 0.06) } }}>My Classes</Box>
        <NavigateNextIcon fontSize="small" sx={{ color: T.ink4 }} />
        <Box component="span" onClick={() => navigate(`/course-management/${courseId}`)} sx={{ color: tPrimary, fontWeight: 600, cursor: "pointer", borderRadius: "6px", px: "6px", py: "4px", "&:hover": { background: alpha(tPrimary, 0.06) } }}>{courseLabel}</Box>
        <NavigateNextIcon fontSize="small" sx={{ color: T.ink4 }} />
        <Box component="span" onClick={() => navigate(`/course-management/${courseId}/attendance`)} sx={{ color: tPrimary, fontWeight: 600, cursor: "pointer", borderRadius: "6px", px: "6px", py: "4px", "&:hover": { background: alpha(tPrimary, 0.06) } }}>Sessions</Box>
        <NavigateNextIcon fontSize="small" sx={{ color: T.ink4 }} />
        <Box component="span" sx={{ color: T.ink2, fontWeight: 600 }}>{sessionLabel} · Attendance</Box>
      </Box>

      {/* Header */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr auto" }, gap: "24px", alignItems: "start", mb: "20px" }}>
        <Box>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: 11, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: T.live, mb: "8px" }}>
            {sState === "Live" && (
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: T.live, position: "relative", "&::before": { content: '""', position: "absolute", inset: "-3px", borderRadius: "50%", bgcolor: T.live, opacity: 0.35, animation: "tcPulse 1.6s ease-out infinite" } }} />
            )}
            {sState === "Live" ? `Live now · ${sessionLabel}` : `${sState} · ${sessionLabel}`}
          </Box>
          <Typography sx={{ fontSize: 27, fontWeight: 800, letterSpacing: "-.025em", color: T.ink, lineHeight: 1.1, fontFamily: "var(--font-display)" }}>
            {courseLabel}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px 20px", mt: "12px", fontSize: 13, color: T.ink2 }}>
            {sessionDateLabel && (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: "7px" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                {sessionDateLabel}
              </Box>
            )}
            {(session?.startTime || session?.endTime) && (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: "7px" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <Box component="span" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, color: T.ink }}>
                  {session?.startTime?.slice(0, 5)} – {session?.endTime?.slice(0, 5)}
                </Box>
              </Box>
            )}
            {session?.location && (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: "7px" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                {session.location}
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0, flexWrap: "wrap" }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: "7px", height: 30, px: "12px", borderRadius: "999px", fontSize: 12, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", background: sStateColor.bg, color: sStateColor.color, border: `1px solid ${sStateColor.br}` }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "currentColor" }} />
            {sState}
          </Box>
          <HowToGuideBox
            iconOnly
            title="How to Track and Manage Session Attendance"
            role="Instructor & Course Coordinators"
            steps={[
              { number: 1, title: "Mark attendance", description: "Click Present, Tardy, No-Show, Call-Out, or Excused for each student." },
              { number: 2, title: "Bulk actions", description: "Click Select All, then choose Mark as… from the dropdown to set the same attendance status for everyone at once." },
              { number: 3, title: "Lock session", description: "When done, lock the session to prevent further changes." },
            ]}
          />
          {canLockSession && (
            <Button
              onClick={handleToggleLock}
              disabled={lockLoading}
              startIcon={sessionLocked ? <LockOpenIcon sx={{ fontSize: 15 }} /> : <LockIcon sx={{ fontSize: 15 }} />}
              sx={{
                height: 40, px: "16px", borderRadius: "10px", fontSize: 13.5, fontWeight: 600,
                bgcolor: T.ink, color: "#fff", textTransform: "none", boxShadow: "none",
                "&:hover": { bgcolor: "#000" },
              }}
            >
              {lockLoading ? "Updating…" : sessionLocked ? "Unlock session" : "Lock session"}
            </Button>
          )}
        </Box>
      </Box>

      {/* Lock message */}
      {lockMessage && (
        <Paper elevation={0} sx={{ mb: 2, p: "12px 16px", borderRadius: "10px", border: `1px solid ${sessionLocked ? alpha(tAccent, 0.20) : T.presentBr}`, bgcolor: sessionLocked ? T.liveBg : T.presentBg }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: sessionLocked ? T.live : T.present }}>{lockMessage}</Typography>
        </Paper>
      )}

      {/* Progress card */}
      <Paper elevation={0} sx={{ bgcolor: T.surface, border: `1px solid ${T.line}`, borderRadius: "14px", p: "16px 20px", mb: "16px", boxShadow: T.shadowSm, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr auto" }, gap: "20px", alignItems: "center" }}>
        <Box>
          <Typography sx={{ fontSize: 12.5, color: T.ink2, mb: "8px" }}>
            <Box component="b" sx={{ color: T.ink, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{markedCount}</Box> of <Box component="b" sx={{ color: T.ink, fontWeight: 700 }}>{totalStudents}</Box> students marked ·{" "}
            <Box component="span" sx={{ color: summary.unmarked === 0 ? T.present : T.ink3 }}>
              {summary.unmarked === 0 ? "All students marked" : `${summary.unmarked} still unmarked`}
            </Box>
          </Typography>
          <Box sx={{ height: 10, borderRadius: "999px", overflow: "hidden", display: "flex", gap: "2px", bgcolor: T.line2 }}>
            {STATUS_KEYS.map((k) => {
              const pct = totalStudents > 0 ? (summary[k] / totalStudents) * 100 : 0;
              return <Box key={k} sx={{ height: "100%", width: `${pct}%`, bgcolor: STATUS_META[k].color, transition: "width .3s" }} />;
            })}
            {summary.unmarked > 0 && (
              <Box sx={{ height: "100%", flex: 1, bgcolor: T.line2 }} />
            )}
          </Box>
        </Box>
        <Box sx={{ fontSize: 30, fontWeight: 800, color: T.ink, letterSpacing: "-.03em", fontVariantNumeric: "tabular-nums", lineHeight: 1, textAlign: { xs: "left", md: "right" }, fontFamily: "var(--font-display)" }}>
          {markedPct}%<Typography component="span" sx={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.ink3, mt: "4px" }}>Marked</Typography>
        </Box>
      </Paper>

      {/* Status filters — selected pill uses that status's color */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap", mb: "20px" }}>
        <Box sx={{
          display: "flex", gap: "3px", bgcolor: "#fff", border: `1px solid ${T.line}`, p: "4px",
          borderRadius: "11px", maxWidth: "100%",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          overflowX: { xs: "visible", sm: "auto" },
        }}>
          {filterTabs.map((t) => {
            const active = statusFilter === t.id;
            const tone =
              t.id === "all" ? { color: tPrimary, bg: tPrimary, hoverBg: "var(--color-primary-10)" }
              : t.id === "unmarked" ? { color: T.unmarked, bg: T.unmarked, hoverBg: T.unmarkedBg }
              : STATUS_META[t.id]
                ? { color: STATUS_META[t.id].color, bg: STATUS_META[t.id].color, hoverBg: STATUS_META[t.id].bg }
                : { color: tPrimary, bg: tPrimary, hoverBg: "var(--color-primary-10)" };
            return (
              <Button
                key={t.id}
                onClick={() => setStatusFilter(t.id)}
                size="small"
                sx={{
                  fontSize: "12.5px", fontWeight: 600,
                  color: active ? "#fff" : T.ink3,
                  bgcolor: active ? tone.bg : "transparent",
                  borderRadius: "6px", textTransform: "none", px: "13px", py: "7px",
                  minWidth: "auto", flexShrink: 0, whiteSpace: "nowrap",
                  display: "inline-flex", alignItems: "center", gap: "7px",
                  "&:hover": {
                    color: active ? "#fff" : tone.color,
                    bgcolor: active ? tone.bg : tone.hoverBg,
                  },
                }}
              >
                {t.id !== "all" && (
                  <Box sx={{
                    width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                    bgcolor: active ? "rgba(255,255,255,0.95)" : tone.color,
                    opacity: active ? 1 : 0.7,
                  }} />
                )}
                {t.label}
                <Box component="span" sx={{
                  fontSize: "11px", fontWeight: 700, px: "7px", py: "1px", borderRadius: "999px",
                  bgcolor: active ? "rgba(255,255,255,0.22)" : T.line3,
                  color: active ? "#fff" : T.ink3,
                  fontVariantNumeric: "tabular-nums",
                }}>
                  {t.count}
                </Box>
              </Button>
            );
          })}
        </Box>
      </Box>

      {/* Roster card */}
      <Paper elevation={0} sx={{ bgcolor: T.surface, border: `1px solid ${T.line}`, borderRadius: "16px", overflow: "hidden", boxShadow: T.shadowSm }}>
        {/* Toolbar — search/count left, actions right; stack on mobile */}
        <Box sx={{
          display: "flex", gap: "12px", alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between", flexWrap: "wrap", flexDirection: { xs: "column", sm: "row" },
          p: "14px 18px", borderBottom: `1px solid ${T.line2}`, bgcolor: "#fff",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap", flex: 1, minWidth: 0, width: { xs: "100%", sm: "auto" } }}>
            <Box sx={{
              display: "flex", alignItems: "center", gap: "9px", height: 36, px: "12px",
              bgcolor: "#fff", border: `1px solid ${T.line}`, borderRadius: "8px",
              minWidth: 0, width: { xs: "100%", sm: 220 }, maxWidth: { xs: "100%", sm: 280 },
            }}>
              <SearchIcon sx={{ fontSize: 16, color: T.ink4, flexShrink: 0 }} />
              <Box
                component="input"
                type="text"
                placeholder="Search students…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ border: 0, outline: 0, font: "inherit", fontSize: "13px", color: T.ink, background: "transparent", width: "100%" }}
              />
            </Box>
            <Typography sx={{ fontSize: 13, color: T.ink3, flex: { xs: "1 1 100%", sm: "0 1 auto" } }}>
              Showing <Box component="b" sx={{ color: T.ink, fontWeight: 700 }}>{filtered.length}</Box> of{" "}
              <Box component="b" sx={{ color: T.ink, fontWeight: 700 }}>{totalStudents}</Box> students
              {statusFilter && statusFilter !== "all" && (
                <Box component="span"> · {STATUS_META[statusFilter]?.label || "Unmarked"}</Box>
              )}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", width: { xs: "100%", sm: "auto" } }}>
            {selectedIds.size > 0 && (
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <Select
                  value=""
                  displayEmpty
                  onChange={(e) => { if (e.target.value) bulkUpdate(e.target.value); }}
                  disabled={!canEditAttendance}
                  sx={{ fontSize: 12.5, fontWeight: 600, borderRadius: "8px", height: 34, bgcolor: T.surface, ".MuiOutlinedInput-notchedOutline": { borderColor: T.line } }}
                  renderValue={() => `Mark ${selectedIds.size}…`}
                >
                  <MenuItem value="" disabled sx={{ fontSize: 12.5, color: T.ink3 }}>Mark selected as…</MenuItem>
                  {STATUS_KEYS.map((k) => (
                    <MenuItem key={k} value={k} sx={{ fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", gap: "8px" }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: STATUS_META[k].color, flexShrink: 0 }} />
                      {STATUS_META[k].label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button
              onClick={markAllPresent}
              disabled={!canEditAttendance || summary.unmarked === 0}
              startIcon={<CheckIcon sx={{ fontSize: 15 }} />}
              sx={{
                height: 34, fontSize: 12.5, fontWeight: 600, textTransform: "none", borderRadius: "8px",
                color: T.ink, border: `1px solid ${T.line}`, bgcolor: "#fff", px: "12px", boxShadow: "none",
                "&:hover": { bgcolor: T.surface2, borderColor: "var(--color-hairline-cool)" },
                "&:disabled": { opacity: 0.45 },
              }}
            >
              Mark all present
            </Button>
            <Button
              startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
              sx={{
                height: 34, fontSize: 12.5, fontWeight: 600, textTransform: "none", borderRadius: "8px",
                color: T.ink, border: `1px solid ${T.line}`, bgcolor: "#fff", px: "12px", boxShadow: "none",
                "&:hover": { bgcolor: T.surface2, borderColor: "var(--color-hairline-cool)" },
              }}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Column head */}
        <Box sx={{ display: { xs: "none", lg: "grid" }, gridTemplateColumns: "24px minmax(140px, 1.1fr) minmax(70px, 0.45fr) minmax(420px, 2.6fr) minmax(100px, 0.75fr) minmax(90px, 0.7fr) 44px", gap: "12px", alignItems: "center", p: "11px 18px", borderBottom: `1px solid ${T.line2}`, fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.ink3, bgcolor: T.surface }}>
          <Box onClick={() => (allSelected ? clearSelection() : selectAllVisible())} sx={{ cursor: "pointer", display: "grid", placeItems: "center", width: 18, height: 18, borderRadius: "5px", border: `1.5px solid ${allSelected ? tPrimary : T.line}`, background: allSelected ? tPrimary : T.surface, color: allSelected ? "#fff" : "transparent", transition: "all .12s", "&:hover": { borderColor: tPrimary } }}>
            {allSelected && <CheckIcon sx={{ fontSize: 11 }} />}
          </Box>
          <Box>Student</Box>
          <Box>Agency ID</Box>
          <Box>Attendance Status</Box>
          <Box>Sign-In</Box>
          <Box>Sign-Out</Box>
          <Box sx={{ textAlign: "right" }}>Edit</Box>
        </Box>

        {/* Rows */}
        <Box>
          {filtered.length === 0 && (
            <Box sx={{ py: 6, textAlign: "center", color: T.ink3 }}>
              <Typography variant="body2">
                {records.length === 0
                  ? "No students enrolled in this class."
                  : "No students match the current filter."}
              </Typography>
            </Box>
          )}
          {filtered.map((record) => {
            const status = getRecordStatus(record);
            const displayName = record.name || record.studentName || `User ${record.userId}`;
            const signInTime = formatTimeOnly(record.signInTime || record.checkedInAt);
            const signOutTime = formatTimeOnly(record.signOutTime || record.checkedOutAt);
            const signInLoc = record.checkinLocation || record.checkinIp || null;
            const signOutLoc = record.checkoutLocation || record.checkoutIp || null;
            const isSelected = selectedIds.has(record.userId);
            const isAuto = record.checkinIp != null; // heuristic: kiosk check-ins have IP

            return (
              <Box
                key={record.userId}
                sx={{
                  display: { xs: "grid", lg: "grid" },
                  gridTemplateColumns: { xs: "24px 36px 1fr auto", lg: "24px minmax(140px, 1.1fr) minmax(70px, 0.45fr) minmax(420px, 2.6fr) minmax(100px, 0.75fr) minmax(90px, 0.7fr) 44px" },
                  gridTemplateAreas: { xs: '"cbx avatar name actions" ". . agency agency" ". . status status" ". . times times"', lg: "none" },
                  position: "relative",
                  gap: { xs: "12px", lg: "12px" },
                  alignItems: "center",
                  p: { xs: "16px 18px", lg: "13px 18px" },
                  borderBottom: `1px solid ${T.line3}`,
                  transition: "background .12s",
                  background: !status ? "linear-gradient(90deg, rgba(123,132,153,.06), transparent 40%)" : "transparent",
                  "&:hover": { bgcolor: T.surface2 },
                  "&:last-child": { borderBottom: 0 },
                }}
              >
                {/* Checkbox */}
                <Box
                  onClick={() => toggleSelect(record.userId)}
                  sx={{
                    gridArea: { xs: "cbx", lg: "auto" },
                    cursor: "pointer",
                    display: "grid",
                    placeItems: "center",
                    width: 18,
                    height: 18,
                    borderRadius: "5px",
                    border: `1.5px solid ${isSelected ? tPrimary : T.line}`,
                    background: isSelected ? tPrimary : T.surface,
                    color: isSelected ? "#fff" : "transparent",
                    transition: "all .12s",
                    flexShrink: 0,
                  }}
                >
                  {isSelected && <CheckIcon sx={{ fontSize: 11 }} />}
                </Box>

                {/* Avatar area (mobile only) */}
                <Box sx={{ gridArea: { xs: "avatar", lg: "auto" }, display: { xs: "block", lg: "none" } }} />

                {/* Selection highlight indicator */}
                <Box sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "3px",
                  bgcolor: isSelected ? tPrimary : "transparent",
                  transition: "background .12s",
                }} />

                {/* Student */}
                <Box sx={{ gridArea: { xs: "name", lg: "auto" }, display: "grid", gridTemplateColumns: { xs: "36px 1fr", lg: "36px 1fr" }, gap: "12px", alignItems: "center", minWidth: 0 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: "50%", color: "#fff", fontSize: 12, fontWeight: 700,
                    display: "grid", placeItems: "center", letterSpacing: "-.01em", flexShrink: 0,
                    background: avatarBg(displayName),
                  }}>
                    {initials(displayName)}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayName}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: T.ink3, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {record.unitLabel || record.agencyName || record.email || "—"}
                    </Typography>
                  </Box>
                </Box>

                {/* Agency ID */}
                <Box sx={{ gridArea: { xs: "agency", lg: "auto" } }}>
                  <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, color: record.agencyId ? T.ink2 : T.ink4 }}>
                    {record.agencyId || "—"}
                  </Typography>
                </Box>

                {/* Status segmented control — wrap on narrow screens */}
                <Box sx={{ gridArea: { xs: "status", lg: "auto" }, minWidth: 0, width: { xs: "100%", lg: "auto" } }}>
                  <Box sx={{
                    display: "inline-flex", flexWrap: { xs: "wrap", lg: "nowrap" }, gap: "2px", p: "3px",
                    bgcolor: T.surface3, border: `1px solid ${T.line}`, borderRadius: "10px",
                    maxWidth: "100%",
                  }}>
                    {STATUS_KEYS.map((k) => {
                      const isOn = status === k;
                      const sm = STATUS_META[k];
                      return (
                        <Button
                          key={k}
                          onClick={() => canEditAttendance && updateStatus(record, k)}
                          disabled={!canEditAttendance}
                          sx={{
                            fontSize: 11, fontWeight: 600, color: isOn ? sm.color : T.ink3,
                            background: isOn ? sm.bg : "transparent",
                            border: `1px solid ${isOn ? sm.br : "transparent"}`,
                            px: "8px", py: "5px", borderRadius: "7px", minWidth: "auto", textTransform: "none", lineHeight: 1,
                            boxShadow: isOn ? T.shadowSm : "none",
                            cursor: canEditAttendance ? "pointer" : "not-allowed",
                            opacity: canEditAttendance ? 1 : 0.5,
                            flexShrink: 0,
                            "&:hover": canEditAttendance ? { background: isOn ? sm.bg : "rgba(255,255,255,.7)", color: isOn ? sm.color : T.ink2 } : {},
                            display: "inline-flex", alignItems: "center", gap: "5px", whiteSpace: "nowrap",
                          }}
                        >
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor", opacity: isOn ? 1 : 0.45, flexShrink: 0 }} />
                          {sm.label}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>

                {/* Sign-in */}
                <Box sx={{ gridArea: { xs: "times", lg: "auto" }, display: { xs: "grid", lg: "contents" } }}>
                  <Box>
                    {signInTime ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", alignItems: "flex-start" }}>
                        <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, color: T.ink, fontVariantNumeric: "tabular-nums" }}>{signInTime}</Typography>
                        {signInLoc && (
                          <Typography sx={{ fontSize: 11, color: T.ink3, display: "inline-flex", alignItems: "center", gap: "3px" }}>
                            <LocationOnIcon sx={{ fontSize: 11, color: T.ink4, flexShrink: 0 }} />{signInLoc}
                          </Typography>
                        )}
                        <Box sx={{
                          mt: "1px", fontSize: 10, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
                          px: "7px", py: "2px", borderRadius: "999px", lineHeight: 1.3,
                          color: isAuto ? T.present : T.tardy,
                          bgcolor: isAuto ? T.presentBg : T.tardyBg,
                          border: `1px solid ${isAuto ? T.presentBr : T.tardyBr}`,
                        }}>
                          {isAuto ? "Auto" : "Manual"}
                        </Box>
                      </Box>
                    ) : (
                      <Typography sx={{ color: T.ink4, fontSize: 13 }}>—</Typography>
                    )}
                  </Box>

                  {/* Sign-out */}
                  <Box>
                    {signOutTime ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                        <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, color: T.ink, fontVariantNumeric: "tabular-nums" }}>{signOutTime}</Typography>
                        {signOutLoc && (
                          <Typography sx={{ fontSize: 11, color: T.ink3, display: "inline-flex", alignItems: "center", gap: "4px" }}>
                            <LocationOnIcon sx={{ fontSize: 11, color: T.ink4, flexShrink: 0 }} />{signOutLoc}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography sx={{ color: T.ink4, fontSize: 13 }}>—</Typography>
                    )}
                  </Box>
                </Box>

                {/* Actions */}
                <Box sx={{ gridArea: { xs: "actions", lg: "auto" }, display: "flex", justifyContent: "flex-end" }}>
                  <IconButton
                    size="small"
                    disabled={!canEditAttendance}
                    sx={{ width: 32, height: 32, borderRadius: "8px", border: "1px solid transparent", color: T.ink3, "&:hover": { bgcolor: T.surface, borderColor: T.line, color: T.ink } }}
                  >
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Footer */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "13px 18px", borderTop: `1px solid ${T.line2}`, bgcolor: T.surface2, fontSize: 12.5, color: T.ink3 }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: "7px", color: savedPulse ? T.present : T.ink3, fontWeight: 600, transition: "color .2s" }}>
            <CheckIcon sx={{ fontSize: 14 }} />
            {savedPulse ? "Saved" : "All changes saved automatically"}
          </Box>
          <Box>Sign-in window closes when session is locked</Box>
        </Box>
      </Paper>


    </Box>
  );
}
